import { useEffect } from "react";
import { Printer, FileDown, Link2, Mail, Linkedin, Globe, UserRoundPlus } from "lucide-react";
import { siteConfig } from "../config";
import { usePageMeta, useSafeLink, copyToClipboard } from "./common";
import content from "../content/recruiter_one_pager_content.json";

export default function OnePager() {
  const safeLink = useSafeLink();
  usePageMeta({
    title: "Poojan Patel — Recruiter One-Pager",
    description: "One-page recruiter summary: manufacturing automation, Product Quality Systems, QMS, RCA, IoT and continuous improvement.",
    canonicalPath: "/recruiter-one-pager",
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12" data-testid="one-pager">
      <div className="flex flex-wrap gap-3 mb-6 no-print" data-testid="one-pager-actions">
        <button
          data-testid="onepager-print-button"
          onClick={() => window.print()}
          className="flex items-center gap-2 bg-cyan text-navy font-sans font-semibold text-sm px-5 py-2.5 rounded-sm hover:bg-pale transition-colors"
        >
          <Printer className="w-4 h-4" aria-hidden="true" /> Print / Save as PDF
        </button>
        <button
          data-testid="onepager-resume-button"
          onClick={() => safeLink(siteConfig.resumeUrl, "Resume", siteConfig.resumeDownloadFilename)}
          className="flex items-center gap-2 border border-cyan/50 text-cyan font-sans text-sm px-5 py-2.5 rounded-sm hover:bg-cyan/10 transition-colors"
        >
          <FileDown className="w-4 h-4" aria-hidden="true" /> Download Resume
        </button>
        <button
          data-testid="onepager-vcard-button"
          onClick={() => safeLink(siteConfig.vcardUrl, "Contact card", siteConfig.vcardDownloadFilename)}
          className="flex items-center gap-2 border border-teal/40 text-pale/80 font-sans text-sm px-5 py-2.5 rounded-sm hover:border-cyan/60 hover:text-paper transition-colors"
        >
          <UserRoundPlus className="w-4 h-4" aria-hidden="true" /> Save Contact
        </button>
        <button
          data-testid="onepager-copy-link-button"
          onClick={() => copyToClipboard(siteConfig.portfolioBaseUrl, "Portfolio link")}
          className="flex items-center gap-2 border border-teal/40 text-pale/80 font-sans text-sm px-5 py-2.5 rounded-sm hover:border-cyan/60 hover:text-paper transition-colors"
        >
          <Link2 className="w-4 h-4" aria-hidden="true" /> Copy Portfolio Link
        </button>
      </div>

      <div className="print-page panel p-8 lg:p-12" data-testid="one-pager-document">
        <header className="border-b border-cyan/20 pb-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan print-accent">{content.pageTitle}</p>
          <h1 className="font-sans tracking-tight text-3xl text-paper mt-2">{siteConfig.name}</h1>
          <p className="font-sans text-lg text-cyan print-accent mt-1">{content.headline}</p>
          <p className="text-sm text-steel mt-2 leading-relaxed">{content.tagline}</p>
          <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-4 text-xs font-mono">
            <a href={siteConfig.emailAddress ? `mailto:${siteConfig.emailAddress}` : undefined} className="flex items-center gap-1.5 text-pale/80 hover:text-cyan print-accent" data-testid="onepager-email-link">
              <Mail className="w-3.5 h-3.5" aria-hidden="true" /> {siteConfig.emailAddress}
            </a>
            <a href={siteConfig.linkedinUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-pale/80 hover:text-cyan print-accent" data-testid="onepager-linkedin-link">
              <Linkedin className="w-3.5 h-3.5" aria-hidden="true" /> linkedin.com/in/poojanpatel14
            </a>
            <a href={siteConfig.portfolioBaseUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-pale/80 hover:text-cyan print-accent" data-testid="onepager-portfolio-link">
              <Globe className="w-3.5 h-3.5" aria-hidden="true" /> {siteConfig.domainDisplay}
            </a>
            <span className="text-steel">{siteConfig.location}</span>
          </div>
        </header>

        <section className="mt-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan print-accent">Profile</p>
          <p className="text-sm text-pale/85 mt-2 leading-relaxed">{content.summary}</p>
        </section>

        <section className="mt-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan print-accent">Measured Impact</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
            {content.impact.map((m) => (
              <div key={m.label} className="border border-cyan/20 rounded-sm p-3" data-testid={`onepager-impact-${m.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <p className="font-mono text-xl text-cyan print-accent">{m.value}</p>
                <p className="font-sans text-xs font-semibold text-paper mt-1">{m.label}</p>
                <p className="text-[10px] text-steel mt-1 leading-snug">{m.detail}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan print-accent">Core Capabilities</p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {content.capabilities.map((c) => (
              <span key={c} className="font-mono text-[10px] px-2 py-1 rounded-sm border border-teal/40 text-pale/85">
                {c}
              </span>
            ))}
          </div>
        </section>

        <section className="mt-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan print-accent">Representative Projects</p>
          <ul className="grid sm:grid-cols-2 gap-x-6 mt-2">
            {content.projects.map((p) => (
              <li key={p} className="flex items-start gap-2 text-xs text-pale/85 py-1">
                <span className="w-1 h-1 rounded-full bg-cyan mt-1.5 shrink-0 print-accent" aria-hidden="true" /> {p}
              </li>
            ))}
          </ul>
        </section>

        <footer className="mt-6 pt-4 border-t border-cyan/20">
          <p className="text-[10px] text-steel leading-relaxed">
            Portfolio demonstrations use clearly labelled synthetic datasets. No proprietary employer data is displayed. Full interactive case studies:
            {siteConfig.domainDisplay}
          </p>
        </footer>
      </div>
    </div>
  );
}
